/**
 * ErrorState — renders an ERROR_UI descriptor (title/hint) + one recover action.
 *
 * The screen decides what each recover verb DOES; this component just resolves
 * the code → {title_uz, hint_uz, recover} and renders the matching button,
 * calling onRecover(recover) when tapped.
 *
 * Props:
 *   code        string  — an error code (see lib/errors.js)
 *   onRecover   fn(recover) — called with 'retry'|'settings'|'manager'|'home'
 *   recover?    string  — override the descriptor's recover verb (e.g. force
 *                         'manager' after 3 face fails)
 *   icon?       string  — override the emoji glyph
 *   severity?   'bad'|'warn'|'grey' — override the icon colour ring
 *
 * Emits an 'error' haptic on mount is the CALLER's job (screens own haptics).
 */
import { errorUI } from '../lib/errors.js';
import { T } from '../lib/i18n.js';
import Button from './Button.jsx';

const RECOVER_LABEL = {
  retry: T.btn_retry,
  settings: T.btn_open_settings,
  manager: T.btn_call_manager,
  home: T.btn_back_home,
};

// Default icon + colour per recover verb (screens can override).
const RECOVER_ICON = {
  retry: { icon: '🔄', severity: 'warn' },
  settings: { icon: '⚙️', severity: 'bad' },
  manager: { icon: '🧑‍💼', severity: 'bad' },
  home: { icon: '↩️', severity: 'grey' },
};

const RECOVER_BTN_VARIANT = {
  retry: 'primary',
  settings: 'primary',
  manager: 'secondary',
  home: 'secondary',
};

export default function ErrorState({ code, onRecover, recover, icon, severity }) {
  const ui = errorUI(code);
  const verb = recover || ui.recover || 'retry';
  const deco = RECOVER_ICON[verb] || RECOVER_ICON.retry;
  const glyph = icon || deco.icon;
  const sev = severity || deco.severity;

  return (
    <div className="err">
      <div className={`err__icon ${sev}`}>{glyph}</div>
      <div className="err__title">{ui.title_uz}</div>
      <div className="err__hint">{ui.hint_uz}</div>
      {onRecover ? (
        <div style={{ width: '100%', maxWidth: 320, marginTop: 6 }}>
          <Button
            variant={RECOVER_BTN_VARIANT[verb] || 'primary'}
            onClick={() => onRecover(verb)}
          >
            {RECOVER_LABEL[verb]}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
